import React, { useState, useEffect } from 'react';
import { getFieldMappings } from '../services/api';

function MappingTable({ title, mappings, contact }) {
	const mappedFields = mappings
		.filter(m => m.crmFieldApiName && m.crmFieldApiName.trim())
		.map(m => {
			const crmValue = contact[m.crmFieldApiName];
			return {
				equifaxField: m.equifaxField,
				crmField: m.crmFieldApiName,
				value: crmValue !== undefined && crmValue !== null && crmValue !== '' ? crmValue : null,
				isRequired: m.isRequired === true || m.isRequired === 'true'
			};
		});

	const populatedFields = mappedFields.filter(f => f.value !== null);
	const missingRequired = mappedFields.filter(f => f.isRequired && f.value === null);
	const missingOptional = mappedFields.filter(f => !f.isRequired && f.value === null);

	return (
		<div style={{ marginBottom: '1.5rem' }}>
			<h4 style={{ marginBottom: '0.5rem', color: '#2d3748' }}>{title}</h4>
			<table className="details-table">
				<thead>
					<tr>
						<th>API Field</th>
						<th>CRM Field</th>
						<th>Value</th>
					</tr>
				</thead>
				<tbody>
					{populatedFields.map((field) => (
						<tr key={field.equifaxField}>
							<td className="label">
								{field.equifaxField}
								{field.isRequired && <span className="required-dot" title="Required"> *</span>}
							</td>
							<td className="crm-field">{field.crmField}</td>
							<td>{String(field.value)}</td>
						</tr>
					))}
				</tbody>
			</table>

			{missingRequired.length > 0 && (
				<div className="message error" style={{ marginTop: '0.5rem' }}>
					<strong>Missing required fields:</strong>
					<ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
						{missingRequired.map(f => (
							<li key={f.equifaxField}>
								{f.equifaxField} (mapped to <code>{f.crmField}</code>)
							</li>
						))}
					</ul>
				</div>
			)}

			{missingOptional.length > 0 && (
				<details style={{ marginTop: '0.5rem' }}>
					<summary className="hint">{missingOptional.length} optional field(s) not populated</summary>
					<ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#666' }}>
						{missingOptional.map(f => (
							<li key={f.equifaxField}>
								{f.equifaxField} → <code>{f.crmField}</code>
							</li>
						))}
					</ul>
				</details>
			)}
		</div>
	);
}

export default function ClientDetails({ contact, onRunCheck, loading, kountReady }) {
	const [equifaxMappings, setEquifaxMappings] = useState([]);
	const [kountMappings, setKountMappings] = useState([]);
	const [loadingMappings, setLoadingMappings] = useState(true);

	useEffect(() => {
		Promise.all([
			getFieldMappings('equifax').catch(() => []),
			getFieldMappings('kount_nao').catch(() => [])
		]).then(([eqData, kountData]) => {
			setEquifaxMappings(eqData);
			setKountMappings(kountData);
		}).finally(() => setLoadingMappings(false));
	}, []);

	// Check for missing required fields across both mappings
	const allMappings = [...equifaxMappings, ...kountMappings];
	const missingRequired = allMappings
		.filter(m => m.crmFieldApiName && m.crmFieldApiName.trim())
		.filter(m => {
			const isRequired = m.isRequired === true || m.isRequired === 'true';
			if (!isRequired) return false;
			const val = contact[m.crmFieldApiName];
			return val === undefined || val === null || val === '';
		});

	return (
		<div className="client-details">
			<h3>Contact Found</h3>
			<p className="subtitle">CRM Record ID: {contact.id}</p>

			{loadingMappings ? (
				<p>Loading field mappings...</p>
			) : (
				<>
					<MappingTable
						title="Kount NAO Fields"
						mappings={kountMappings}
						contact={contact}
					/>

					<MappingTable
						title="Equifax Report Fields"
						mappings={equifaxMappings}
						contact={contact}
					/>
				</>
			)}

			<div className="action-bar">
				<button
					onClick={onRunCheck}
					disabled={loading || missingRequired.length > 0}
					className="btn-primary"
				>
					{loading ? 'Processing...' : 'Run Credit Check'}
				</button>
				{missingRequired.length > 0 && (
					<span className="hint" style={{ color: '#c0392b' }}>
						Cannot proceed — required fields are missing in CRM
					</span>
				)}
				{!kountReady && missingRequired.length === 0 && (
					<span className="hint">Device fingerprinting in progress...</span>
				)}
			</div>
		</div>
	);
}
